import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApplicationData, UpdateState } from 'src/types/flow';

const INITIAL_STATE: UpdateState = {
  initiatingUpdateFlow: false,
  initiatingUpdateFlowError: '',

  updateLicenseInProgress: false,
  updateLicenseError: '',

  documentUploadStarted: false,
  documentUploadWorking: false,
  documentUploadCompleted: false,
  documentUploadErrors: [],

  applicationData: {},
  applicationNumber: '',
};

export const updateSlice = createSlice({
  name: 'update',
  initialState: INITIAL_STATE,
  // Reducers describe operations on state. Under the hood, reducers take the
  // current state and modify it in some manner then return the modified state.
  // Redux stores the modified state as the current state.  Subsequent reducers
  // will then operate on the modified state - thus, state changes accumulate/persist
  reducers: {
    setInitiatingUploadFlowInProgress: (state: UpdateState) => {
      state.initiatingUpdateFlow = true;
      state.initiatingUpdateFlowError = '';
      state.documentUploadStarted = false;
      state.documentUploadWorking = false;
      state.documentUploadCompleted = false;
    },
    setInitiatingUploadFlowComplete: (
      state: UpdateState,
      action: PayloadAction<string>
    ) => {
      state.initiatingUpdateFlow = false;
      state.initiatingUpdateFlowError = action.payload;
    },
    setUpdateLicenseInProgress: (state: UpdateState) => {
      state.updateLicenseInProgress = true;
      state.initiatingUpdateFlowError = '';
      state.documentUploadErrors = [];
    },
    setUpdateLicenseCompleted: (state: UpdateState) => {
      state.updateLicenseInProgress = false;
    },
    setUpdateLicenseError: (
      state: UpdateState,
      action: PayloadAction<string>
    ) => {
      state.updateLicenseError = action.payload;
    },
    updatePermitData: (
      state: UpdateState,
      action: PayloadAction<ApplicationData>
    ) => {
      state.applicationData = {
        ...state.applicationData,
        ...action.payload,
      };
    },
    removeRegistrantData: (state: UpdateState) => {
      if (state.applicationData) delete state.applicationData.registrant;
    },
    removeContactsData: (state: UpdateState) => {
      if (state.applicationData) delete state.applicationData.contacts;
    },
    removePropertyDetailsData: (state: UpdateState) => {
      if (state.applicationData) delete state.applicationData.propertyDetails;
    },
    removeListingsData: (state: UpdateState) => {
      if (state.applicationData) delete state.applicationData.listings;
    },
    setUpdateApplicationNumber: (state, action) => {
      state.applicationNumber = action.payload;
    },
    setDocumentUploadStarted: (
      state: UpdateState,
      action: PayloadAction<boolean>
    ) => {
      state.documentUploadErrors = [];
      state.documentUploadWorking = false;
      state.documentUploadCompleted = false;
      state.documentUploadStarted = action.payload;
    },
    setDocumentUploadWorking: (
      state: UpdateState,
      action: PayloadAction<boolean>
    ) => {
      state.documentUploadStarted = false;
      state.documentUploadCompleted = false;
      state.documentUploadWorking = action.payload;
    },
    setDocumentUploadCompleted: (
      state: UpdateState,
      action: PayloadAction<boolean>
    ) => {
      state.documentUploadStarted = false;
      state.documentUploadWorking = false;
      state.documentUploadCompleted = action.payload;
    },
    setDocumentUploadError: (
      state: UpdateState,
      action: PayloadAction<string>
    ) => {
      state.documentUploadErrors = [
        ...state.documentUploadErrors,
        action.payload,
      ];
    },
    clearDocumentUploadError: (state: UpdateState) => {
      state.documentUploadErrors = [];
    },
    resetDocumentUploadProgressStatus: (state: UpdateState) => {
      state.documentUploadStarted = false;
      state.documentUploadWorking = false;
      state.documentUploadCompleted = false;
    },
    resetUpdateState: () => INITIAL_STATE,
  },
});

export const {
  setInitiatingUploadFlowInProgress,
  setInitiatingUploadFlowComplete,
  updatePermitData,
  removeRegistrantData,
  removeContactsData,
  removeListingsData,
  removePropertyDetailsData,
  setUpdateApplicationNumber,
  setDocumentUploadStarted,
  setDocumentUploadWorking,
  setDocumentUploadCompleted,
  setDocumentUploadError,
  resetDocumentUploadProgressStatus,
  resetUpdateState,
  setUpdateLicenseInProgress,
  setUpdateLicenseCompleted,
  setUpdateLicenseError,
} = updateSlice.actions;

export default updateSlice.reducer;
